// Airgent Operators roster — 6 AI roles. Used in Hero pile, Roster section, Waitlist chips.
// Photos: DiceBear "personas" illustrated avatars. Swap `photo` URLs to use your own files.

export type Operator = {
  id: string;
  name: string;
  role: string;
  photo: string;
  status: 'hiring-now' | 'interviewing' | 'planned';
  color: string;
  bg: string;
  hook: string;
  replaces: string;
  cost: string;
  speed: string;
  quality: string;
  runs: string;
  exp: string;
  languages: string[];
  stack: string[];
  deliverables: string[];
  day: { t: string; e: string }[];
  model: string;
  build: string;
  uptime: string;
};

export const ROSTER: Operator[] = [
  {
    id: 'priya', name: 'Priya Desai', role: 'Listings Specialist',
    photo: 'https://api.dicebear.com/9.x/personas/svg?seed=priya-airgent-04',
    status: 'hiring-now', color: '#C06E47', bg: '#F5E8DF',
    hook: 'Writes Shopify listings that actually sell.',
    replaces: 'Content writer · ₹28K/mo',
    cost: '$0.05', speed: '90s', quality: '0.86', runs: '188',
    exp: '14 months · 4.8k listings',
    languages: ['English', 'Hindi', 'Hinglish'],
    stack: ['Shopify', 'BigCommerce', 'WooCommerce'],
    deliverables: ['Title', 'Long description', 'Bullets', 'SEO meta', 'Tags', 'Variant copy'],
    day: [
      { t: '09:14', e: 'CSV with 240 SKUs dropped in drive.' },
      { t: '09:15', e: 'Classifies by category, pulls brand voice.' },
      { t: '09:17', e: '240 listings drafted · 11 fields each · quality-scored.' },
      { t: '09:18', e: '4 flagged for review. Rest auto-published.' },
    ],
    model: 'claude-sonnet-4', build: 'v3.4.1', uptime: '99.7%',
  },
  {
    id: 'arjun', name: 'Arjun Khanna', role: 'Support Triage',
    photo: 'https://api.dicebear.com/9.x/personas/svg?seed=arjun-airgent-08',
    status: 'hiring-now', color: '#4A6FA5', bg: '#E4ECF6',
    hook: 'Reads every ticket. Answers the easy 70%.',
    replaces: 'Tier-1 support · ₹22K/mo',
    cost: '$0.02', speed: '12s', quality: '0.91', runs: '1.2k',
    exp: '8 months · 24k tickets',
    languages: ['English', 'Hindi', 'Tamil', 'Marathi'],
    stack: ['Zendesk', 'Freshdesk', 'Intercom'],
    deliverables: ['Intent tag', 'Sentiment', 'Draft reply', 'Escalate/resolve', 'CRM update'],
    day: [
      { t: '08:00', e: 'Overnight 47 tickets in Zendesk inbox.' },
      { t: '08:01', e: 'Tags intent, drafts replies for 33.' },
      { t: '08:03', e: '14 escalated with context summary.' },
      { t: '08:04', e: 'Lead starts the day with 14, not 47.' },
    ],
    model: 'gpt-4o', build: 'v2.8.0', uptime: '99.4%',
  },
  {
    id: 'meera', name: 'Meera Iyer', role: 'GST Classifier',
    photo: 'https://api.dicebear.com/9.x/personas/svg?seed=meera-airgent-12',
    status: 'hiring-now', color: '#2F6B4D', bg: '#E0EFE6',
    hook: 'HSN codes in 4 seconds. CA-grade accuracy.',
    replaces: 'Junior accountant · ₹18K/mo',
    cost: '$0.01', speed: '4s', quality: '0.94', runs: '3.4k',
    exp: '11 months · 68k SKUs classified',
    languages: ['English', 'Hindi'],
    stack: ['Tally', 'Zoho Books', 'ClearTax'],
    deliverables: ['HSN code', 'GST rate', 'Rationale', 'Audit trail', 'Edge-case flag'],
    day: [
      { t: '14:00', e: 'EOD batch · 312 new SKUs.' },
      { t: '14:00', e: 'Pulls product + images, classifies.' },
      { t: '14:02', e: '308 coded at 0.94+ confidence. 4 flagged.' },
      { t: '14:03', e: 'CA reviews 4. Tally ledger updated.' },
    ],
    model: 'claude-haiku-4', build: 'v1.9.2', uptime: '99.9%',
  },
  {
    id: 'kabir', name: 'Kabir Malhotra', role: 'Lead Enricher',
    photo: 'https://api.dicebear.com/9.x/personas/svg?seed=kabir-airgent-19',
    status: 'interviewing', color: '#7C5CB0', bg: '#EEE7F7',
    hook: 'Turns a name + company into a qualified lead.',
    replaces: 'SDR · ₹35K/mo',
    cost: '$0.08', speed: '45s', quality: '0.79', runs: '62',
    exp: 'Beta · 62 runs',
    languages: ['English'],
    stack: ['Apollo', 'LinkedIn Sales Nav', 'Clearbit', 'HubSpot'],
    deliverables: ['Firmographics', 'Tech stack', 'Intent signals', 'ICP fit score', 'Personalised opener'],
    day: [{ t: '—', e: 'Interviewing with 3 design-partner teams. Join to observe.' }],
    model: 'claude-opus-4', build: 'v0.6-beta', uptime: '87.2%',
  },
  {
    id: 'neha', name: 'Neha Kapoor', role: 'Content Writer',
    photo: 'https://api.dicebear.com/9.x/personas/svg?seed=neha-airgent-22',
    status: 'interviewing', color: '#B85C38', bg: '#F7E5DC',
    hook: 'SEO-first blog posts. Brand voice locked.',
    replaces: 'Freelance writer · ₹40K/mo',
    cost: '$0.12', speed: '3m', quality: '0.82', runs: '41',
    exp: 'Beta · 41 posts',
    languages: ['English', 'Hindi'],
    stack: ['WordPress', 'Ghost', 'Webflow'],
    deliverables: ['1200-word post', 'Meta + schema', 'Internal links', '3 social variants', 'Hero brief'],
    day: [{ t: '—', e: 'Second-round interviews · early-adopter slots open.' }],
    model: 'claude-sonnet-4', build: 'v0.4-beta', uptime: '92.1%',
  },
  {
    id: 'rohan', name: 'Rohan Mehta', role: 'Inventory Ops',
    photo: 'https://api.dicebear.com/9.x/personas/svg?seed=rohan-airgent-31',
    status: 'planned', color: '#556B8D', bg: '#E3E8F0',
    hook: 'Stock-outs predicted before they happen.',
    replaces: 'Ops analyst · ₹30K/mo',
    cost: '—', speed: '—', quality: '—', runs: '—',
    exp: 'In training',
    languages: ['English', 'Hindi'],
    stack: ['Unicommerce', 'EasyEcom', 'Shopify'],
    deliverables: ['Reorder alerts', 'Velocity by SKU', 'Dead-stock flag', 'PO drafts'],
    day: [{ t: '—', e: 'Q2 2026. Résumé on file.' }],
    model: 'gpt-4o-mini', build: 'training', uptime: '—',
  },
];
